
import { useEffect, useState } from "react"
import axios from "axios"
import tokenService from "../../../services/token-service"
import apiUrls from "../../../config/api-urls"
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts"



const TasksSection = () => {
  const [tasks, setTasks] = useState({
    totalEarning: 0,
    total: 0,
    completed: 0,
    cancelled: 0,
    pending: 0,
    disputed: 0,
  })
  const [isLoading, setIsLoading] = useState(true)

  const chartData = [
    { name: "Total Earning", value: isLoading ? 0 : Number(tasks.totalEarning) || 0, color: "#10b981" },
    { name: "Completed", value: isLoading ? 0 : Number(tasks.completed) || 0, color: "#3b82f6" },
    { name: "Cancelled", value: isLoading ? 0 : Number(tasks.cancelled) || 0, color: "#ef4444" },
  ]

  useEffect(() => {
    let isMounted = true

    const fetchDashboard = async () => {
      try {
        const token = tokenService.getToken()
        const response = await axios.get(
          apiUrls.candidate.dashboard,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        )

        const data = response?.data?.data || {}
        const nextTasks = {
          totalEarning: data.totalEarnings || 0,
          total: data.totalJobs || 0,
          completed: data.completedTasks || 0,
          cancelled: data.cancelledJobs || 0,
          pending: data.pendingJobs || 0,
          disputed: data.disputedJobs || 0,
        }

        if (isMounted) {
          setTasks(nextTasks)
        }
      } catch {
        if (isMounted) {
          setTasks({
            totalEarning: 0,
            total: 0,
            completed: 0,
            cancelled: 0,
            pending: 0,
            disputed: 0,
          })
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    fetchDashboard()

    return () => {
      isMounted = false
    }
  }, [])

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md border border-gray-200 dark:border-slate-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Your Tasks</h2>
      </div>

      {/* Task Stats Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 border border-emerald-200 dark:border-emerald-700">
          <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium uppercase tracking-wide">Total Earning</p>
          <p className="text-2xl font-bold text-emerald-900 dark:text-emerald-100 mt-1 flex items-center gap-2">
            PKR {isLoading ? 0 : tasks.totalEarning}
          </p>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
          <p className="text-xs text-blue-600 dark:text-blue-400 font-medium uppercase tracking-wide">Total</p>
          <p className="text-2xl font-bold text-blue-900 dark:text-blue-100 mt-1">{isLoading ? 0 : tasks.total}</p>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-700">
          <p className="text-xs text-green-600 dark:text-green-400 font-medium uppercase tracking-wide">Complete</p>
          <p className="text-2xl font-bold text-green-900 dark:text-green-100 mt-1">{isLoading ? 0 : tasks.completed}</p>
        </div>

        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-700">
          <p className="text-xs text-red-600 dark:text-red-400 font-medium uppercase tracking-wide">Cancelled</p>
          <p className="text-2xl font-bold text-red-900 dark:text-red-100 mt-1">{isLoading ? 0 : tasks.cancelled}</p>
        </div>

        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-700">
          <p className="text-xs text-amber-600 dark:text-amber-400 font-medium uppercase tracking-wide">Pending</p>
          <p className="text-2xl font-bold text-amber-900 dark:text-amber-100 mt-1">{isLoading ? 0 : tasks.pending}</p>
        </div>

        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-200 dark:border-orange-700">
          <p className="text-xs text-orange-600 dark:text-orange-400 font-medium uppercase tracking-wide">Disputed</p>
          <p className="text-2xl font-bold text-orange-900 dark:text-orange-100 mt-1">{isLoading ? 0 : tasks.disputed}</p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md border border-gray-200 dark:border-slate-700 p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Task Statistics</h2>

        <div className="h-64 flex items-center justify-center">
          {chartData.every((item) => item.value === 0) ? (
            <div className="text-center">
              <p className="text-gray-500 dark:text-gray-400">No task data available yet</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">Data will appear as you complete tasks</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) =>
                    name === "Total Earning" ? `${name}: PKR ${value}` : `${name}: ${value}`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) =>
                    name === "Total Earning" ? [`PKR ${value}`, name] : [value, name]
                  }
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

    </div>
  )
}

export default TasksSection
